import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Link } from 'gatsby';
import * as JsSearch from 'js-search';
import PageLeftIcon from '../../images/icons/pageLeftIcon';
import PageRightIcon from '../../images/icons/pageRightIcon';
import useTranslations from '../../utils/useTranslations';

const ARTICLES_PER_PAGE = 8;

const HelpCenterSearchIndex = (props) => {
    const [dataToSearch, setDataToSearch] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [filteredResults, setFilteredResults] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [numResults, setNumResults] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);

    const [prevQuery, setPrevQuery] = useState(props.query);
    if (props.query !== prevQuery) {
        setPrevQuery(props.query);
        applySearchQuery(props.query, dataToSearch);
        setPageIndex(0);
    }

    const [prevFilterList, setPrevFilterList] = useState(props.filterList);
    if (
        props.filterList.length !== prevFilterList.length ||
        !props.filterList.every((item, i) => prevFilterList[i] === item)
    ) {
        setPrevFilterList(props.filterList);
        filterSearchResults(searchResults, props.filterList);
        setPageIndex(0);
    }

    useEffect(() => {
        Axios.get('/search-info.json')
            .then((result) => {
                const searchInfo = result.data;
                const data = rebuildIndex(searchInfo.articles);
                setDataToSearch(data);
                applySearchQuery(props.query, data);
            })
            .catch((err) => {
                console.log('====================================');
                console.log(`Something bad happened while fetching the data\n${err}`);
                console.log('====================================');
            });
    }, []);

    function applySearchQuery(query, data) {
        setSearchQuery(query);
        searchData(query, data);
    }

    function filterSearchResults(searchResults, filterList) {
        let filteredResults =
            filterList.length > 0
                ? searchResults.filter((result) => {
                      return filterList.includes(result.productId);
                  })
                : searchResults;
        setFilteredResults(filteredResults);
        setNumResults(filteredResults.length);
    }

    /**
     * rebuilds the overall index based on the options
     */
    function rebuildIndex(articles) {
        const dataToSearch = new JsSearch.Search('id');
        /**
         * defines an indexing strategy for the data
         * more about it in here https://github.com/bvaughn/js-search#configuring-the-index-strategy
         */
        dataToSearch.indexStrategy = new JsSearch.PrefixIndexStrategy();
        /**
         * defines the sanitizer for the search
         * to prevent some of the words from being excluded
         *
         */
        dataToSearch.sanitizer = new JsSearch.LowerCaseSanitizer();
        /**
         * defines the search index
         * read more in here https://github.com/bvaughn/js-search#configuring-the-search-index
         */
        dataToSearch.searchIndex = new JsSearch.TfIdfSearchIndex('id');

        dataToSearch.addIndex('title'); // sets the index attribute for the data
        dataToSearch.addIndex('content'); // sets the index attribute for the data
        dataToSearch.addIndex('productName'); // sets the index attribute for the data

        dataToSearch.addDocuments(articles); // adds the data to be searched
        return dataToSearch;
    }

    /**
     * handles the input change and perform a search with js-search
     * in which the results will be added to the state
     */
    function searchData(query, data) {
        const searchValue = query;
        const queryResult = data.search(searchValue);
        setSearchQuery(searchValue);
        setSearchResults(queryResult);
        filterSearchResults(queryResult, props.filterList);
    }

    function dateDisplay(isoString) {
        let date = new Date(isoString);
        return date.toLocaleString();
    }

    function updatePage(isForward) {
        if (isForward) {
            if ((pageIndex + 1) * ARTICLES_PER_PAGE < filteredResults.length - 1) {
                setPageIndex((index) => index + 1);
            }
        } else {
            if (pageIndex > 0) {
                setPageIndex((index) => index - 1);
            }
        }
    }

    const queryResults =
        searchQuery === ''
            ? []
            : filteredResults.slice(ARTICLES_PER_PAGE * pageIndex, ARTICLES_PER_PAGE * pageIndex + ARTICLES_PER_PAGE);
    const re = new RegExp('(?<!>)\\b' + searchQuery + '\\b(?!>)', 'ig');
    const t = useTranslations(props.text);

    return (
        <div className="results-column">
            {searchQuery !== '' && typeof searchQuery === 'string' && (
                <>
                    <div className="results-header">{t('searchResults', { numResults, searchQuery })}</div>
                    <div className="articles">
                        {queryResults.map((item) => {
                            let highlightedTitle = item.title.replaceAll(re, `<p class="highlighted">$&<p>`);
                            let highlightedContent = item.content.replaceAll(re, '<p class="highlighted">$&<p>');
                            return (
                                <div className="article-result" key={item.id}>
                                    <Link to={item.link}>
                                        <div
                                            className="article-title"
                                            dangerouslySetInnerHTML={{
                                                __html: highlightedTitle,
                                            }}
                                        ></div>
                                        <div className="article-details">
                                            {item.folderName} | {dateDisplay(item.createdAt)}
                                        </div>
                                        <div
                                            className="article-content"
                                            dangerouslySetInnerHTML={{
                                                __html: highlightedContent,
                                            }}
                                        ></div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                    <div className={'page-selectors'}>
                        <button
                            className="page-selector btn-no-styling"
                            disabled={pageIndex <= 0}
                            onClick={() => updatePage(false)}
                        >
                            <PageLeftIcon extraClass={pageIndex > 0 ? '' : 'grey'} />
                        </button>
                        <button
                            className="page-selector btn-no-styling"
                            disabled={(pageIndex + 1) * ARTICLES_PER_PAGE >= filteredResults.length - 1}
                            onClick={() => updatePage(true)}
                        >
                            <PageRightIcon
                                extraClass={
                                    (pageIndex + 1) * ARTICLES_PER_PAGE < filteredResults.length - 1 ? '' : 'grey'
                                }
                            />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default HelpCenterSearchIndex;
